/**
 * Organizer API.
 *
 * Mirrors the engine's organizer surface. Every endpoint here is behind
 * authenticate + requireRole('organizer'), and each mutation additionally
 * verifies organizer_id ownership server-side — the route guard in this app is
 * a usability gate only.
 */

import { api, apiRequest } from './apiClient';

export type EventStatus = 'draft' | 'published' | 'live' | 'closed' | 'cancelled' | 'postponed';

// ── Dashboard ───────────────────────────────────────────────────────────────

export interface OrganizerEvent {
  id: string;
  title: string;
  status: EventStatus;
  date: string | null;
  time: string | null;
  venueName: string | null;
  city: string | null;
  state: string | null;
  featuredImageUrl: string | null;
  genre: string | null;
  category: string | null;
  createdAt: string;
  updatedAt: string;
  overallCompliant: boolean;
  canPublish: boolean;
  blockedReason: 'compliance_incomplete' | null;
  tierCount: number;
  capacity: number;
  /** Held-but-unpaid inventory. Distinct from ticketsSold. */
  reserved: number;
  ticketsSold: number;
  revenueCents: number;
  bookings: number;
}

export const listMyEvents = () => api.get<OrganizerEvent[]>('/events/mine');

// ── Event detail (owner scoped, any status) ─────────────────────────────────

export interface ManagedTier {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  /**
   * An identifier from a system outside Voleto, kept for reconciliation only. Nothing in
   * the engine reads it and nothing joins on it.
   */
  variationRef?: string | null;
  quantityTotal: number;
  quantitySold: number;
  remaining: number;
  status: string;
  saleStart: string | null;
  saleEnd: string | null;
}

export interface ManagedEvent {
  id: string;
  title: string;
  description: string | null;
  featuredImageUrl: string | null;
  /** Optional hero video. The image above stays the fallback and is never replaced by it. */
  featuredVideoUrl: string | null;
  featuredVideoWebmUrl: string | null;
  /** Free-text display fields the organizer types. Separate from the booked venue. */
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  venueState: string | null;
  /**
   * The booked venue PROFILE — a different thing from the free text above. Null
   * until the organizer requests a real venue from the directory. The engine does
   * not copy one into the other, so both can be set and disagree.
   */
  venueProfileId: string | null;
  venueBookingStatus: VenueBookingStatus | null;
  eventDate: string | null;
  eventTime: string | null;
  doorsTime: string | null;
  /** Null for a single day with no stated finish. */
  eventEndDate: string | null;
  eventEndTime: string | null;
  expectedAttendance: number | null;
  ageRestriction: string | null;
  genre: string | null;
  category: string | null;
  status: EventStatus;
  overallCompliant: boolean;
  canPublish: boolean;
  createdAt: string;
  updatedAt: string;
  tiers: ManagedTier[];
}

export const getManagedEvent = (eventId: string) =>
  api.get<ManagedEvent>(`/events/${eventId}/manage`);

// ── Event CRUD ──────────────────────────────────────────────────────────────

export interface EventDraftInput {
  title: string;
  description?: string | null;
  featuredImageUrl?: string | null;
  /** Optional hero video, MP4 relied on, WebM accepted as an extra source. */
  featuredVideoUrl?: string | null;
  featuredVideoWebmUrl?: string | null;
  eventEndDate?: string | null;
  eventEndTime?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  venueState?: string | null;
  eventDate?: string | null;
  eventTime?: string | null;
  doorsTime?: string | null;
  expectedAttendance?: number | null;
  ageRestriction?: string | null;
  genre?: string | null;
  category?: string | null;
}

export const createEvent = (data: EventDraftInput) =>
  api.post<{ id: string; status: EventStatus }>('/events', data);

export const updateEvent = (eventId: string, data: Partial<EventDraftInput>) =>
  api.put<{ id: string; status: EventStatus }>(`/events/${eventId}`, data);

export const publishEvent = (eventId: string) =>
  api.post<{ id: string; status: EventStatus }>(`/events/${eventId}/publish`);

// ── Tiers ───────────────────────────────────────────────────────────────────

export interface TierInput {
  /** External reconciliation reference. Free text; Voleto never interprets it. */
  variationRef?: string | null;
  name: string;
  description?: string | null;
  priceCents: number;
  quantityTotal: number;
  saleStart?: string | null;
  saleEnd?: string | null;
}

export const createTier = (eventId: string, data: TierInput) =>
  api.post<{ id: string }>(`/events/${eventId}/tiers`, data);

export const updateTier = (eventId: string, tierId: string, data: Partial<TierInput> & { status?: string }) =>
  api.put<ManagedTier>(`/events/${eventId}/tiers/${tierId}`, data);

export const deleteTier = (eventId: string, tierId: string) =>
  api.delete<{ deleted: boolean }>(`/events/${eventId}/tiers/${tierId}`);

// ── Compliance (organizer view: read + submit documents) ────────────────────

export const COMPLIANCE_ITEMS = [
  'venue_agreement', 'fire_marshal', 'alcohol_permit', 'insurance',
  'security_plan', 'ada_compliance', 'noise_ordinance', 'sales_tax',
] as const;

export type ComplianceItem = (typeof COMPLIANCE_ITEMS)[number];
export type ComplianceStatus = 'pending' | 'submitted' | 'approved' | 'not_required';

export const ITEM_LABELS: Record<ComplianceItem, string> = {
  venue_agreement: 'Venue agreement',
  fire_marshal: 'Fire marshal sign-off',
  alcohol_permit: 'Alcohol permit',
  insurance: 'Insurance certificate',
  security_plan: 'Security plan',
  ada_compliance: 'ADA compliance',
  noise_ordinance: 'Noise ordinance',
  sales_tax: 'Sales tax registration',
};

export interface Checklist {
  eventId: string;
  items: Record<ComplianceItem, { status: ComplianceStatus; docUrl: string | null }>;
  overallCompliant: boolean;
  updatedAt: string | null;
}

export const getChecklist = (eventId: string) =>
  api.get<Checklist>(`/events/${eventId}/compliance`);

/** Organizers may SUBMIT documentation. Only an admin can move an item to approved. */
export const submitComplianceDoc = (eventId: string, item: ComplianceItem, docUrl: string) =>
  api.post<Checklist>(`/events/${eventId}/compliance/upload`, { item, docUrl });

// ── Sales ───────────────────────────────────────────────────────────────────

export interface SalesSummary {
  [key: string]: unknown;
}

export const getSales = (eventId: string) =>
  apiRequest<SalesSummary>(`/events/${eventId}/sales`, { method: 'GET' });

// ── Artist booking ──────────────────────────────────────────────────────────

/** GET /api/artists — approved artists only. Verified shape, not assumed. */
export interface DirectoryArtist {
  artistId: string;
  stageName: string;
  genre: string | null;
  primaryMarket: string | null;
  profileImageUrl: string | null;
  tier: string | null;
  /** Null until the artist has 5+ performed events — see buildingTrackRecord. */
  reliabilityScore: number | null;
  buildingTrackRecord: boolean;
}

/** GET /api/artists/:id — adds the bio and socials the list omits. */
export interface ArtistProfile extends DirectoryArtist {
  bio: string | null;
  coverImageUrl: string | null;
  followers: number;
  social: {
    instagram: { handle: string | null; followers: number | null };
    tiktok: { handle: string | null; followers: number | null };
    spotifyMonthlyListeners: number | null;
    youtubeSubscribers: number | null;
  };
}

export const browseArtists = (params: { genre?: string; market?: string; tier?: string } = {}) => {
  const q = new URLSearchParams();
  if (params.genre?.trim()) q.set('genre', params.genre.trim());
  if (params.market?.trim()) q.set('market', params.market.trim());
  if (params.tier) q.set('tier', params.tier);
  const query = q.toString();
  return api.get<DirectoryArtist[]>(`/artists${query ? `?${query}` : ''}`);
};

export const getArtist = (artistId: string) => api.get<ArtistProfile>(`/artists/${artistId}`);

/**
 * POST /api/bookings. Verified requirements: performanceFeeCents must be a
 * positive integer, and depositCents must be an integer between 0 and the fee.
 * The organizer is never asked about an agency — routing is a backend concern.
 */
export interface BookingInput {
  eventId: string;
  artistProfileId: string;
  performanceFeeCents: number;
  depositCents: number;
  cancellationPenaltyCents?: number | null;
  performanceStartTime?: string | null;
  setDurationMinutes?: number | null;
  contractTerms?: string | null;
}

export const createBooking = (data: BookingInput) =>
  api.post<{ id: string; contract_number: string; status: string }>('/bookings', data);

/**
 * GET /api/bookings/mine returns raw snake_case rows, unlike the rest of the API.
 * Typed as it actually is rather than as it ought to be.
 */
export interface AgreementRow {
  id: string;
  contract_number: string;
  status: AgreementStatus;
  performance_fee_cents: number;
  deposit_cents: number;
  organizer_signed_at: string | null;
  artist_signed_at: string | null;
  deposit_released: boolean;
  balance_released: boolean;
  event_id: string;
  artist_profile_id: string;
  performance_start_time: string | null;
  set_duration_minutes: number | null;
  created_at: string;
  event_title: string;
  event_date: string | null;
  stage_name: string;
  genre: string | null;
  profile_image_url: string | null;
}

/**
 * The real enum from the engine. Note there is NO 'declined' or 'expired' —
 * an artist declining a request sets the agreement to 'cancelled'.
 */
export type AgreementStatus =
  | 'pending' | 'signed' | 'deposit_paid' | 'performed' | 'cancelled' | 'disputed';

/** Human labels. 'cancelled' reads as declined because that is how it arises here. */
export const AGREEMENT_LABEL: Record<AgreementStatus, string> = {
  pending: 'Awaiting artist',
  signed: 'Signed',
  deposit_paid: 'Deposit paid',
  performed: 'Performed',
  cancelled: 'Declined / cancelled',
  disputed: 'Disputed',
};

export const listMyBookings = () => api.get<AgreementRow[]>('/bookings/mine');

// ── Venue directory (task 38) ───────────────────────────────────────────────

/**
 * GET /api/venues. Authenticated, unlike the artist directory: a venue profile
 * carries pricing and technical specs, which are commercial terms.
 */
export interface DirectoryVenue {
  venueId: string;
  venueName: string;
  city: string | null;
  state: string | null;
  capacity: number | null;
  layoutOptions: string | null;
  technicalSpecs: string | null;
  pricingStructure: string | null;
  requirements: string | null;
  images: string[];
  /**
   * Inert today — no engine path sets it, so the directory does not filter on it.
   * Surfaced so the UI can say "not yet reviewed" rather than imply a pending review.
   */
  approved: boolean;
  /** Bookings this venue has actually confirmed — the only track record venues have. */
  confirmedBookings: number;
  createdAt: string;
}

export interface VenueFilters {
  search?: string;
  city?: string;
  state?: string;
  minCapacity?: number | null;
  maxCapacity?: number | null;
}

export const browseVenues = (filters: VenueFilters = {}) => {
  const q = new URLSearchParams();
  if (filters.search?.trim()) q.set('search', filters.search.trim());
  if (filters.city?.trim()) q.set('city', filters.city.trim());
  if (filters.state?.trim()) q.set('state', filters.state.trim());
  if (filters.minCapacity != null) q.set('minCapacity', String(filters.minCapacity));
  if (filters.maxCapacity != null) q.set('maxCapacity', String(filters.maxCapacity));
  const query = q.toString();
  return api.get<DirectoryVenue[]>(`/venues${query ? `?${query}` : ''}`);
};

export const venueMarkets = () =>
  api.get<{ city: string; state: string | null; venues: number }[]>('/venues/markets');

/**
 * Read a venue's calendar before requesting it. Organizers are allowed this by
 * design — it exposes nothing beyond the calendar itself.
 */
export const venueAvailability = (venueId: string, from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  const query = q.toString();
  return api.get<{
    venueId: string;
    dates: { date: string; available: boolean; notes: string | null; bookedByEvent: string | null }[];
  }>(`/venue/${venueId}/availability${query ? `?${query}` : ''}`);
};

/**
 * POST /api/venue/:id/request — attaches the event to the venue and creates a
 * PENDING booking. The venue then confirms or declines.
 *
 * Refused with 409 venue_already_confirmed if this event already has a confirmed
 * venue; a DECLINED booking can be re-requested.
 */
export const requestVenue = (venueId: string, eventId: string) =>
  api.post<{ eventId: string; venueId: string; status: VenueBookingStatus }>(
    `/venue/${venueId}/request`, { eventId });

/** NULL until an organizer requests. 'confirmed' is the only terminal state. */
export type VenueBookingStatus = 'pending' | 'confirmed' | 'declined';

export const VENUE_BOOKING_LABEL: Record<VenueBookingStatus, string> = {
  pending: 'Awaiting the venue',
  confirmed: 'Venue confirmed',
  declined: 'Venue declined',
};

// ── Payout account (engine migration 066) ───────────────────────────────────
//
// Net proceeds on an event with NO investment raise transfer to the organizer's own
// Stripe Connect account. Before this existed those proceeds stayed in Voleto's platform
// balance and were settled some other way, with nothing on the platform recording that
// the organizer was owed them.
//
// SELF-SERVE, unlike an SPV's or a venue's account, which an admin sets up. The bank
// account being verified is the organizer's own, so the person who has to complete
// Stripe's identity checks is the organizer.

export interface OrganizerPayoutAccount {
  userId: string;
  /** not_started until they begin; complete is not enough on its own, see below. */
  onboardingStatus: 'not_started' | 'pending' | 'complete' | 'restricted';
  /**
   * The only flag that answers the question a transfer actually asks. An account can read
   * complete and still have payouts disabled because verification lapsed.
   */
  canReceiveTransfers: boolean;
  stripeConnectAccountId: string | null;
  /** What Stripe is waiting for, so a restriction is actionable rather than just red. */
  requirementsDue: string[];
  stripeDisabledReason?: string | null;
  payoutsEnabled?: boolean;
  isMock?: boolean;
}

/** What one event owes its organizer, and whether it has been sent. */
export interface OrganizerPayout {
  id: string;
  eventId: string;
  organizerUserId: string;
  grossTicketRevenueCents: number;
  stripeProcessingFeeCents: number;
  voletoPlatformFeeCents: number;
  /** The venue's cut, where the event was published under an arrangement. */
  venueRevenueShareCents: number;
  netProceedsCents: number;
  status: 'pending' | 'processing' | 'paid' | 'reversed' | 'failed';
  stripeTransferId: string | null;
  reversedCents: number;
  attempts: number;
  lastError: string | null;
  transferredAt: string | null;
}

export const getPayoutAccount = () =>
  api.get<OrganizerPayoutAccount>('/settlement/payout-account');

/** Hosted onboarding. The link is short lived by Stripe's design, so it is fetched on demand. */
export const startPayoutOnboarding = (returnBase: string) =>
  api.post<{ url: string; expiresAt: number }>(
    '/settlement/payout-account/onboarding', { returnBase });

/** Our copy is only as fresh as the last read; this asks Stripe directly. */
export const refreshPayoutAccount = () =>
  api.post<OrganizerPayoutAccount>('/settlement/payout-account/refresh', {});

export const getEventPayout = (eventId: string) =>
  api.get<OrganizerPayout | null>(`/settlement/${eventId}/payout`);
